/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { Fragment, useMemo, useState } from "react"
import { Alert, Button, Modal } from "@duxton-web/ui"
import { useTranslationsMap } from "common/utils/i18n"
import { useSelector } from "react-redux"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { useGetMerchantDetailQuery } from "queries"
import { AppState } from "store/store.type"
import styles from "./FpPostSyncBanner.modules.css"

interface ParsedDialogContent {
  paragraphs: string[]
  listItems: string[]
}

const boldTagRegex = /<b>(.*?)<\/b>/g
const listTagRegex = /<li>(.*?)<\/li>/g
const syncDatePlaceholderRegex = /{{\s*sync_date\s*}}/g
const doubleBreakRegex = /\s*<br><br>\s*/g

const replaceSyncDate = (text: string, syncDate: string) =>
  text.replace(syncDatePlaceholderRegex, syncDate)

const parseDialogBody = (text: string): ParsedDialogContent => {
  const normalizedText = text.replace(/\u2028|\u2029/g, "")
  const listItems = Array.from(
    normalizedText.matchAll(listTagRegex),
  ).map(match => match[1].trim())

  const paragraphText = normalizedText
    .replace(listTagRegex, "")
    .replace(doubleBreakRegex, "\n\n")
    .trim()

  const paragraphs = paragraphText
    ? paragraphText
        .split(/\n{2,}/)
        .map(paragraph => paragraph.trim())
        .filter(Boolean)
    : []

  return {
    paragraphs,
    listItems,
  }
}

const renderBoldText = (text: string) => {
  const nodes: React.ReactNode[] = []
  let previousIndex = 0

  boldTagRegex.lastIndex = 0
  let match = boldTagRegex.exec(text)

  while (match) {
    if (match.index > previousIndex) {
      nodes.push(
        <Fragment key={`text-${previousIndex}`}>
          {text.slice(previousIndex, match.index)}
        </Fragment>,
      )
    }

    nodes.push(<strong key={`bold-${match.index}`}>{match[1]}</strong>)

    previousIndex = match.index + match[0].length
    match = boldTagRegex.exec(text)
  }

  if (previousIndex < text.length) {
    nodes.push(
      <Fragment key={`text-${previousIndex}`}>
        {text.slice(previousIndex)}
      </Fragment>,
    )
  }

  return nodes
}

export const FpPostSyncBanner = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Same GrabX kill-switch as mex-portal Store Profile Garnet surfaces.
  const isGarnetMigrationEnabled = !!getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.mexPortalGarnetMigration,
  )
  const merchantID = useSelector((state: AppState) => state.menu.merchantID)
  const { currentData: merchant } = useGetMerchantDetailQuery(
    {
      merchantID,
      showGlobalLoading: false,
      showError: false,
    },
    { skip: !merchantID || !isGarnetMigrationEnabled },
  )
  const fpPostSync = merchant?.fpPostSync

  const textMap = useTranslationsMap({
    bannerBody: "menu.postSync.banner.body",
    learnMore: "menu.postSync.banner.learnMore",
    dialogTitle: "menu.postSync.dialog.title",
    dialogBody: "menu.postSync.dialog.body",
    gotIt: "menu.postSync.dialog.gotIt",
  })

  const syncDate = fpPostSync?.fullSyncDate ?? ""
  const bannerBody = replaceSyncDate(textMap.bannerBody, syncDate)
  const dialogTitle = replaceSyncDate(textMap.dialogTitle, syncDate)
  const parsedDialogBody = useMemo(
    () => parseDialogBody(replaceSyncDate(textMap.dialogBody, syncDate)),
    [textMap.dialogBody, syncDate],
  )

  if (!isGarnetMigrationEnabled || !fpPostSync?.enableTopUp) {
    return null
  }

  return (
    <>
      <Alert
        className={styles.banner}
        type="warning"
        showIcon
        data-testid="menu-fp-post-sync-banner"
        message={
          <div className={styles.bannerContent}>
            <span className={styles.bannerBody}>{bannerBody}</span>
            <Button
              type="link"
              size="small"
              className={styles.learnMoreButton}
              data-testid="menu-fp-post-sync-banner-learn-more"
              onClick={() => setIsModalOpen(true)}
            >
              {textMap.learnMore}
            </Button>
          </div>
        }
      />

      <Modal
        title={<div className={styles.dialogTitle}>{dialogTitle}</div>}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        centered
        destroyOnClose
        data-testid="menu-fp-post-sync-dialog"
        footer={[
          <Button
            key="got-it"
            type="primary"
            onClick={() => setIsModalOpen(false)}
          >
            {textMap.gotIt}
          </Button>,
        ]}
      >
        <div className={styles.dialogBody}>
          {parsedDialogBody.paragraphs.map(paragraph => (
            <p className={styles.paragraph} key={paragraph}>
              {renderBoldText(paragraph)}
            </p>
          ))}

          {!!parsedDialogBody.listItems.length && (
            <ul className={styles.list}>
              {parsedDialogBody.listItems.map(listItem => (
                <li className={styles.listItem} key={listItem}>
                  {renderBoldText(listItem)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>
    </>
  )
}
